import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { transferDataToDatabase } from "@/api/onboardingImport";
import { useOnboarding } from "@/context/OnboardingContext";

export default function useAuthentication() {
  const navigate = useNavigate();
    const { state } = useOnboarding();

  // Handle signup
  const handleSignup = async (email: string, password: string): Promise<void> => {
    try {
      console.log("Starting signup process...");

      // Generate organisation ID if not exists
      const organisationId = localStorage.getItem("organisation_id") || uuidv4();
      const websiteUrl = state.websiteUrl;
      const urlWithoutProtocol = websiteUrl.replace(/^https?:\/\//, "");
      const domainParts = urlWithoutProtocol.split(".");
      const organisationName =
        domainParts.length > 1 ? domainParts[domainParts.length - 2] : urlWithoutProtocol;

      // Store organisation info
      const organisationInfo = {
        id: organisationId,
        name: organisationName.charAt(0).toUpperCase() + organisationName.slice(1),
        created_at: new Date().toISOString(),
      };

      console.log("Storing organisation info:", organisationInfo);
      localStorage.setItem("organisation_id", organisationId);
      localStorage.setItem("organisation_info", JSON.stringify(organisationInfo));

      // Also store the full organisation object for the OrganisationContext
      console.log(
        "Storing full organisation data in localStorage for OrganisationContext"
      );
      localStorage.setItem("currentOrganisation", JSON.stringify(organisationInfo));

      // Use hardcoded URL for production (moved to app subdomain)
      const redirectUrl =
        "https://app.contentgardener.ai/dashboard?onboarding=complete&transfer=true";
      console.log("Using redirect URL:", redirectUrl);

      // Sign up with email verification and proper redirect
      const { data: signUpData, error: signupError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            website_url: websiteUrl,
            organisation_name: organisationName,
          },
          emailRedirectTo: redirectUrl,
        },
      });

      if (signupError) {
        console.error("Signup error:", signupError);
        throw signupError;
      }

      if (!signUpData.user) {
        throw new Error("Failed to create user");
      }

      console.log("User created successfully:", signUpData.user.id);

      // Store signup data
      localStorage.setItem("pending_user_id", signUpData.user.id);
      localStorage.setItem("signup_email", email);

      // Store signup data for later
      localStorage.setItem(
        "pending_signup",
        JSON.stringify({
          userId: signUpData.user.id,
          email,
          timestamp: new Date().toISOString(),
        })
      );

      // Sign in the user immediately
      const { data: signInData, error: signInError } =
        await supabase.auth.signInWithPassword({
          email,
          password,
        });

      if (signInError) {
        console.error("Sign in error:", signInError);
        throw signInError;
      }

      if (!signInData.user) {
        throw new Error("Failed to sign in after signup");
      }

      console.log("User signed in successfully:", signInData.user.id);

      // Create organisation first
      console.log("Creating organisation with data:", organisationInfo);

      // First, verify the organisation doesn't already exist
      const { data: existingOrg, error: checkError } = await supabase
        .from("organisations")
        .select("id")
        .eq("id", organisationId)
        .single();

      if (checkError && checkError.code !== "PGRST116") {
        // PGRST116 is "not found"
        console.error("Error checking existing organisation:", checkError);
        throw new Error("Failed to check existing organisation");
      }

      if (!existingOrg) {
        console.log("Organisation does not exist, creating new one...");
        const { error: orgError } = await supabase
          .from("organisations")
          .insert([organisationInfo]);

        if (orgError) {
          console.error("Organisation creation error:", orgError);
          throw new Error("Failed to create organisation");
        }

        // Add a small delay to ensure database consistency
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Verify organisation was created
        const { data: verifyOrg, error: verifyError } = await supabase
          .from("organisations")
          .select("id")
          .eq("id", organisationId)
          .single();

        if (verifyError || !verifyOrg) {
          console.error("Organisation verification error:", verifyError);
          throw new Error("Failed to verify organisation creation");
        }

        console.log("Organisation created and verified successfully");
      } else {
        console.log("Organisation already exists, skipping creation");
      }

      // Double check organisation exists before proceeding
      const { data: finalCheck, error: finalCheckError } = await supabase
        .from("organisations")
        .select("id")
        .eq("id", organisationId)
        .single();

      if (finalCheckError || !finalCheck) {
        console.error("Final organisation check failed:", finalCheckError);
        throw new Error("Organisation not found after creation");
      }

      console.log("Organisation setup complete, proceeding with data transfer");

      // Now transfer data which will create membership and other data
      await transferDataToDatabase(signInData.user.id);

      // Update this toast call to use Sonner's format
      toast.success(
        "Welcome to Content Gardener! Your account has been created and you're ready to start creating content."
      );

      // Add a small delay to ensure data is properly stored before navigation
      setTimeout(() => {
        console.log("Navigating to dashboard after successful signup");
        // Force a reload of the page to ensure the organisation data is properly loaded
        window.location.href = "/dashboard";
      }, 1000);
    } catch (error) {
      console.error("Signup error:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to create account";
      toast.error("Signup Error: " + errorMessage);
      throw error;
    }
  };

  // Handle Google signup
  const handleOnGoogleSignup = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) throw error;

      // The OAuth flow will handle the redirect
    } catch (error) {
      console.error("Google signup error:", error);
      throw error;
    }
  };

  // Complete onboarding and go to dashboard
  const handleComplete = () => {
    const userId = localStorage.getItem("pending_user_id");
    if (userId) {
      console.log("Transferring data for user:", userId);
      transferDataToDatabase(userId)
        .then(async () => {
          toast.success(
            "Setup Complete! Your account has been created and you're ready to start creating content."
          );
          
          // Start intelligence agent after successful data transfer
          try {
            const websiteId = localStorage.getItem("website_id");
            if (websiteId) {
              console.log("Starting intelligence agent for website:", websiteId);
              await startIntelligenceAgent(websiteId);
            }
          } catch (error) {
            console.error("Error starting intelligence agent:", error);
            // Don't show error to user as this is background process
          }
          
          navigate("/dashboard");
        })
        .catch((error) => {
          console.error("Error transferring data:", error);
          toast.error(
            "Error completing setup: " +
              (error.message || "Failed to complete setup. Please try again.")
          );
        });
    } else {
      console.error("No user ID found for data transfer");
      toast.error("Setup Error: User ID not found. Please try signing up again.");
    }
  };

  // Start intelligence agent for concept generation
  const startIntelligenceAgent = async (websiteId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        console.warn("No access token found for intelligence agent");
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/keyword-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          website_id: websiteId,
          action: 'start'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to start intelligence agent');
      }

      const result = await response.json();
      if (result.success) {
        console.log("Intelligence agent started successfully:", result.message);
      } else {
        console.warn("Intelligence agent response:", result.error);
      }
    } catch (error) {
      console.error("Error starting intelligence agent:", error);
    }
  };

  return {
    handleSignup,
    handleOnGoogleSignup,
    handleComplete,
  };
}
